import React, { useEffect, useState, useMemo } from 'react';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import PageHeader from '../Header/PageHeader';
import { useSnackbar } from '../../utils/useSnackbar';
import { getFeedback, updateFeedbackStatus } from '../../utils/api/feedback_apis';
import { getOrganizations } from '../../utils/api/organization_apis';
import { STATUS } from '../../utils/api/sharedfunction';
import FeedbackFilter from './FeedbackFilter';
import FeedbackTable from './FeedbackTable';
import pageStyles from '../../styles/feedback/FeedbackPage.module.scss';
import { getFeedbackTypeLabel, getFeedbackStatusLabel } from '../../utils/feedbackUtils';

const PAGE_CONTENT = {
  title: 'User Feedback',
  description: 'Review and manage feedback submitted by users.',
  breadcrumbs: { admin: 'Administration', page: 'User Feedback' },
  emptyState: 'No feedback submissions yet.',
  loadingError: 'Failed to load feedback.',
};

const FILTER_LABELS = {
  type: 'Type',
  status: 'Status',
  org_id: 'Organisation',
  start_date: 'Start Date',
  end_date: 'End Date',
};

const FeedbackList = () => {
  const activePage = useSelector((state) => state.navigation.activePage);
  const { showSnackbar } = useSnackbar();

  const [filters, setFilters] = useState({
    type: [],
    status: '',
    org_id: '',
    start_date: '',
    end_date: '',
  });
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [feedbackData, setFeedbackData] = useState({ total: 0, data: [] });
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const breadcrumbItems = [
    { label: PAGE_CONTENT.breadcrumbs.admin },
    { label: activePage },
  ];

  // Reset to first page when filters change
  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [filters.type, filters.status, filters.org_id, filters.start_date, filters.end_date]);

  // Fetch feedback when filters or pagination change
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const page = paginationModel.page + 1;
      const params = {
        page,
        pageSize: paginationModel.pageSize,
        ...(filters.type?.length ? { type: filters.type.join(',') } : {}),
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.org_id ? { org_id: filters.org_id } : {}),
        ...(filters.start_date ? { start_date: filters.start_date } : {}),
        ...(filters.end_date ? { end_date: filters.end_date } : {}),
      };
      const result = await getFeedback(params);
      if (result.status === STATUS.SUCCESS) {
        const payload = result.data || {};
        setFeedbackData({
          total: payload.total ?? (Array.isArray(payload) ? payload.length : 0),
          data: Array.isArray(payload) ? payload : (payload.data ?? []),
        });
      } else {
        showSnackbar(result.error || PAGE_CONTENT.loadingError, 'error');
      }
      setLoading(false);
    };
    fetchData();
  }, [filters, paginationModel]);

  // Fetch organizations on mount
  useEffect(() => {
    const fetchOrgs = async () => {
      const result = await getOrganizations();
      if (result.status === STATUS.SUCCESS) {
        setOrganizations(result.data || []);
      }
    };
    fetchOrgs();
  }, []);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleStatusUpdate = async (feedbackId, newStatus, previousStatus) => {
    // Optimistic update
    setFeedbackData((prev) => ({
      ...prev,
      data: prev.data.map((item) =>
        item.id === feedbackId ? { ...item, status: newStatus } : item,
      ),
    }));
    const result = await updateFeedbackStatus(feedbackId, newStatus);
    if (result.status !== STATUS.SUCCESS) {
      // Revert on failure
      setFeedbackData((prev) => ({
        ...prev,
        data: prev.data.map((item) =>
          item.id === feedbackId ? { ...item, status: previousStatus } : item,
        ),
      }));
      showSnackbar(result.error || 'Failed to update status', 'error');
    }
  };

  // Compute active filter chips from current filters
  const activeFilterChips = useMemo(() => {
    const chips = [];
    const { type, status, org_id, start_date, end_date } = filters;

    if (Array.isArray(type) && type.length > 0) {
      type.forEach((val, idx) => {
        chips.push({ key: `type_${idx}`, label: FILTER_LABELS.type, value: getFeedbackTypeLabel(val) });
      });
    }
    if (status) {
      chips.push({ key: 'status', label: FILTER_LABELS.status, value: getFeedbackStatusLabel(status) });
    }
    if (org_id) {
      const org = organizations.find((o) => String(o.id ?? o.value) === String(org_id));
      chips.push({ key: 'org_id', label: FILTER_LABELS.org_id, value: org?.name ?? org?.label ?? org_id });
    }
    if (start_date) {
      chips.push({
        key: 'start_date',
        label: FILTER_LABELS.start_date,
        value: new Date(start_date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }),
      });
    }
    if (end_date) {
      chips.push({
        key: 'end_date',
        label: FILTER_LABELS.end_date,
        value: new Date(end_date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }),
      });
    }
    return chips;
  }, [filters, organizations]);

  return (
    <Box className={pageStyles.pageContainer}>
      <PageHeader
        title={PAGE_CONTENT.title}
        description={PAGE_CONTENT.description}
        breadcrumbItems={breadcrumbItems}
      />
      <Box className={pageStyles.sidePanelContainer}>
        <Box className={pageStyles.tableWrapper}>
          <FeedbackTable
            data={feedbackData.data}
            total={feedbackData.total}
            loading={loading}
            paginationModel={paginationModel}
            onPaginationChange={setPaginationModel}
            onStatusUpdate={handleStatusUpdate}
          />
        </Box>
        <FeedbackFilter
          filters={filters}
          onFilterChange={handleFilterChange}
          organizations={organizations}
          isCollapsed={isCollapsed}
          onToggleCollapse={() => setIsCollapsed((prev) => !prev)}
          activeFilterChips={activeFilterChips}
        />
      </Box>
    </Box>
  );
};

export default FeedbackList;
